/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from "react";
import Search from "@react/react-spectrum/Search";
import Textfield from "@react/react-spectrum/Textfield";
import SearchInputUICss from "./SearchInputUI.css";
import { NumberOfSearchResultsContainer } from "containers/search/NumberOfSearchResultsContainer";
import ButtonWrapper from "dc-preview-utils/src/components/Button/ButtonWrapper";
import SearchIcon from "dc-icons/global/s_search_18.svg";
import SearchIconMobile from "dc-icons/viewer2021/Web_Mobile/SDC_Search_22_N.svg";
import { Text } from "@react-spectrum/text";
import { ActionButton } from "@react-spectrum/button";
import { Icon } from "@react-spectrum/icon";
import { MenuTrigger, Menu, Item } from "@react-spectrum/menu";
import More from "dc-icons/genai/SDC_More_18_N.svg";
import * as codes from "dc-preview-utils/src/Constants/key-codes";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { isSinglePageMode } from "selectors/documentSelectors";
import { F, ESCAPE, TAB, ENTER } from "dc-preview-utils/src/Constants/key-codes";
import { trapFocus } from "dc-preview-utils/src/Utils/FocusUtils";
import classNames from "classnames";
import { FormattedMessage } from "react-intl";
import { isControlORCommandPressed } from "dc-preview-utils/src/Utils/EventHandlingUtils";
import { isCurrentDeviceMobile, isCurrentDeviceTablet } from "dc-preview-utils/src/Utils/DeviceUtils";
import { SearchProgressBarSmall } from "components/search/SearchProgressBarSmall";
import { overlayEvents } from "overlays/OverlayEvents";
import SearchInputDarkThemeUI from "./SearchInputDarkThemeUI.css";
import { getViewPortWidth } from "selectors/documentViewSelectors";
import { getCustomCss, shouldUseFullWidthOnTablet } from "selectors/searchSelectors";
import * as stores from "stores";
import { getTotalPages } from "selectors/documentSelectors";
import { isSafari } from "dc-preview-utils/src/Utils/UserAgentUtils";
import CancelSearchButton from "components/search/CancelSearchButton";
import { TOOLTIP_BOTTOM_PLACEMENT, TOOLTIP_DELAY, INTERACTIVE_ARIA_ROLES } from "constants/constants";
import { searchAddOnPanelTrigger } from "constants/enums";
import { debounce } from "utils/lodashUtils";
import { SEARCH_INPUT_FIELD_FOCUS_BLUR_TIMEOUT, SEARCH_MENU_ITEMS_UPDATE_TIMEOUT } from "constants/constants";
import { trackSearchTripleDotMenuAppeared, trackSearchTripleDotMenuClicked } from "analytics/events";
import CustomIcon from "dc-preview-utils/src/components/CustomIcon";

class SearchInputUI extends React.Component {
  constructor(props) {
    super(props);
    this.nextClicked = this.nextClicked.bind(this);
    this.previousClicked = this.previousClicked.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.closeClickHandler = this.closeClickHandler.bind(this);
    this.state = {
      isSearchFieldFocused: false,
      addOnPanelComponent: null,
      hideAISuggestionOnEnter: false,
      searchText: "",
      isTripleDotEventCalled: false,
      isTripleDotClicked: false,
      shouldShowPanelonTripleDotClick: false,
      addOnMenuItems: []
    };
  }

  setSearchFieldFocused = debounce(() => {
    this.setState({ isSearchFieldFocused: true });
    this.setState({ hideAISuggestionOnEnter: false });
  }, SEARCH_INPUT_FIELD_FOCUS_BLUR_TIMEOUT);

  setSearchFieldBlurred = debounce(() => {
    if (document.activeElement === this.textField) return;
    if (this.state.isTripleDotClicked) {
      this.setState({ shouldShowPanelonTripleDotClick: true });
    }
    this.setState({ isSearchFieldFocused: false });
  }, SEARCH_INPUT_FIELD_FOCUS_BLUR_TIMEOUT);

  componentDidUpdate() {
    if (!this.state.isTripleDotClicked && this.state.shouldShowPanelonTripleDotClick) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ shouldShowPanelonTripleDotClick: false });
    }
  }

  componentDidMount() {
    this.triggerElementId = document.activeElement.id;
    overlayEvents.trigger("findUIStatus", true);

    const searchInputUIClassname = this.props.useModernViewer
      ? SearchInputUICss.SearchInputUITopBarModernViewer
      : SearchInputUICss.SearchInputUITopBar;
    if (this.rootElement) {
      let topbarSearch = document.getElementsByClassName(searchInputUIClassname);
      if (
        topbarSearch &&
        topbarSearch[0] &&
        (isCurrentDeviceMobile() ||
          (shouldUseFullWidthOnTablet(stores) && (isCurrentDeviceTablet() || getViewPortWidth(stores) < 800)))
      ) {
        topbarSearch[0].style.width = "100%";
        topbarSearch[0].style["max-width"] = "100%";
      }

      this.textField = this.rootElement.getElementsByClassName("spectrum-Textfield")[0];
      this.textField.style.width = "100%";
      if (isCurrentDeviceMobile() || isCurrentDeviceTablet()) {
        this.textField.style.fontSize = "16px";
      }
      if (this.props.shouldUseMobileModernViewer) {
        this.textField.style.paddingLeft = "40px";
      }
      let spectrumSearchContainer = this.rootElement.getElementsByClassName("spectrum-Search")[0];
      if (spectrumSearchContainer) {
        spectrumSearchContainer.style.width = "100%";
      }
      if (getIsInPlaceMode(stores)) {
        this.textField.value = this.props.getSearchQueryFromStore();
      }
      let selectionEnd = 0;
      if (this.textField.value) {
        selectionEnd = this.textField.value.length;
      }
      // on safari focus is applied by default
      // applying focus again causes unwanted scroll of window
      if (!isSafari()) this.textField.focus();
      this.textField.setSelectionRange(selectionEnd, selectionEnd);
      this.wasPreviousKeyDownEscape = false;
      window.addEventListener("keydown", this.onKeydown);

      this.getSearchAddOnPanel();
      this.setState({ searchText: this.props.defaultSearchQuery });
    }
  }

  componentWillUnmount() {
    // remove the added event listener to avoid memory
    // leaks and other undesired side effects
    if (!getIsInPlaceMode(stores)) {
      this.props.clearSearchUI();
      this.closeClickHandler();
      overlayEvents.trigger("findUIStatus", false);
      this.textField = null;
    } else {
      const query = this.textField.value ? this.textField.value : "";
      this.props.setSearchQueryInStore(query);
    }
    window.removeEventListener("keydown", this.onKeydown);
  }

  onKeydown(event) {
    let keyDownHandled = false;
    const { searchAddOnPanel } = this.props;
    switch (event.keyCode) {
      case F:
        if (isControlORCommandPressed(event)) {
          event.preventDefault();
          if (this.textField) {
            if (!isSafari()) this.textField.focus();
            keyDownHandled = true;
          }
        }
        break;
      case ESCAPE:
        if (document.activeElement === this.textField) {
          if (this.wasPreviousKeyDownEscape === false) {
            this.wasPreviousKeyDownEscape = true;
          } else {
            this.closeClickHandler();
          }
        } else {
          this.closeClickHandler();
        }
        keyDownHandled = true;
        break;
      case TAB:
        if (this.props.useModernViewer) {
          trapFocus(this.rootElement, INTERACTIVE_ARIA_ROLES, event);
          keyDownHandled = true;
        }
        break;
      case ENTER:
        if (searchAddOnPanel) this.setState({ hideAISuggestionOnEnter: true });
        break;
      default:
        if (this.wasPreviousKeyDownEscape === true) {
          this.wasPreviousKeyDownEscape = false;
        }
        break;
    }
    if (!keyDownHandled) {
      if (document.activeElement === this.textField) {
        this.props.onKeydown(event, this.textField.value, true /* infocus*/);
      } else if (this.textField) {
        this.props.onKeydown(event, this.textField.value);
      }
    }
  }

  previousClicked() {
    if (this.textField && this.textField.value.length > 0) this.props.onPreviousClicked(this.textField.value);
  }

  nextClicked() {
    if (this.textField && this.textField.value.length > 0) this.props.onNextClicked(this.textField.value);
  }

  closeClickHandler() {
    if (getIsInPlaceMode(stores)) {
      this.textField.value = "";
    }
    if (this.props.useModernViewer && this.triggerElementId) {
      // as global-nav re-renders on closing of searchInputUI, focussing element with delay of a frame
      window.requestAnimationFrame(() => {
        const triggerElement = document.getElementById(this.triggerElementId);
        if (triggerElement && document.activeElement !== triggerElement) {
          triggerElement.focus();
        }
      });
    }
    this.props.closeClickHandler();
  }

  searchFieldFocusHandler = event => {
    this.props.onFocus && this.props.onFocus(event);
    this.props.setShouldApplyFocusOnThumbnail(false);
    this.setSearchFieldFocused();
  };

  searchFieldBlurHandler = event => {
    this.props.onBlur && this.props.onBlur(event);
    this.props.setShouldApplyFocusOnThumbnail(true);
    this.setSearchFieldBlurred();
  };

  shouldShowTripleDot = () => {
    const { isTripleDotEventCalled } = this.state;
    if (this.state.addOnPanelComponent) {
      if (!isTripleDotEventCalled) {
        this.setState({ isTripleDotEventCalled: true });
        trackSearchTripleDotMenuAppeared();
      }
      return true;
    }
    return false;
  };

  shouldShowSearchAddOnPanel = () => {
    const { searchAddOnPanel } = this.props;
    if (searchAddOnPanel && this.state.addOnPanelComponent && !this.state.hideAISuggestionOnEnter) {
      if (
        searchAddOnPanel.trigger === searchAddOnPanelTrigger.ALWAYS ||
        (searchAddOnPanel.trigger === searchAddOnPanelTrigger.ON_FOCUS &&
          (this.state.isSearchFieldFocused || this.state.shouldShowPanelonTripleDotClick))
      ) {
        return true;
      }
    }
    return false;
  };

  getSearchAddOnPanel = () => {
    const { searchAddOnPanel } = this.props;
    if (searchAddOnPanel) {
      searchAddOnPanel.callback().then(component => {
        this.setState({ addOnPanelComponent: component });
      });
      this.setState({ addOnMenuItems: searchAddOnPanel.getTripleDotMenuItems() });
    }
  };

  render() {
    const { searchInputClassName, searchWidgetClassName, searchWidgetStyle, searchAddOnPanel } = this.props;
    let availableMaxRight = 73;
    let marginRight = 0;
    const searchInputStyle = Object.assign({}, this.props.searchInputStyle);
    Object.assign(searchInputStyle, getCustomCss(stores));

    if (this.props.useModernViewer) {
      if (getIsInPlaceMode(stores)) {
        searchInputStyle.padding = "8px 0px";
      } else if (isSinglePageMode(stores)) {
        searchInputStyle.padding = "8px 0px 8px 4px";
      } else if (this.props.shouldUseMobileModernViewer) {
        searchInputStyle.padding = "15px 8px";
      } else {
        searchInputStyle.padding = "7px 0px 12px 0px";
      }

      availableMaxRight = window.innerWidth - this.props.docViewRects.right;
      searchInputStyle.right = `${availableMaxRight}px`;
      searchInputStyle.marginRight = this.props.scrollbarSize === 0 ? "20px" : `${8 + this.props.scrollbarSize}px`;
      marginRight = searchInputStyle.marginRight;
    } else {
      // In dc web height of topbar is bigger than that in view sdk, hence we need different padding in both cases
      searchInputStyle.padding = getIsInPlaceMode(stores) ? "0px" : getCustomCss(stores).padding;
    }

    // when no of pages > 99 HUD does not provide padding, leaving margin for the search box
    if (getIsInPlaceMode(stores) && getTotalPages(stores) > 99) {
      searchInputStyle.margin = "0px 8px";
    }

    const isMobile = isCurrentDeviceMobile();

    let numberOfSearchResultsInDocument = null;
    if (this.props.showNumberOfSearchResults) {
      numberOfSearchResultsInDocument = <NumberOfSearchResultsContainer themeColor={this.props.options.color} />;
    } else if (this.props.showSearchProgressBar) {
      numberOfSearchResultsInDocument = <SearchProgressBarSmall />;
    }

    let searchGoToGroupClassName = this.props.useModernViewer
      ? SearchInputUICss.SearchGoToButtonGroupModernViewer
      : SearchInputUICss.SearchGoToButtonGroup;

    let classNextIcon = this.props.useModernViewer ? SearchInputUICss.NextIconModernViewer : SearchInputUICss.NextIcon;

    let classSearchUI = null,
      classPreviousIcon = null,
      searchWidgetStyleTheme = null,
      classSearchGoToButtonGroup = null;
    if (this.props.options.color === "dark") {
      if (getIsInPlaceMode(stores)) {
        classSearchUI = SearchInputDarkThemeUI.InlineSearchTextInput;
        searchWidgetStyleTheme = { width: "190px", display: "flex" };
        classPreviousIcon = SearchInputDarkThemeUI.inPlacePreviousIcon;
        classNextIcon = SearchInputDarkThemeUI.inPlaceNextIcon;
        classSearchGoToButtonGroup = SearchInputDarkThemeUI.inPlaceSearchGoToButtonGroup;
      } else {
        classSearchUI = SearchInputDarkThemeUI.SearchTextInput;
        searchWidgetStyleTheme = { paddingTop: "3px" };
        classNextIcon = SearchInputDarkThemeUI.NextIcon;
        classPreviousIcon = SearchInputDarkThemeUI.PreviousIcon;
        classSearchGoToButtonGroup = SearchInputDarkThemeUI.SearchGoToButtonGroup;
      }
    } else {
      classSearchUI = SearchInputUICss.SearchTextInput;
      searchWidgetStyleTheme = searchWidgetStyle;
      classSearchGoToButtonGroup = searchGoToGroupClassName;
    }

    if (this.props.useModernViewer && isSinglePageMode(stores)) {
      searchWidgetStyleTheme = { width: "100%", display: "flex" };
    }
    let classSearchGoToButtonGroupActive = this.props.useModernViewer
      ? SearchInputUICss.SearchGoToButtonGroupActiveWidth
      : SearchInputDarkThemeUI.SearchGoToButtonGroupActiveWidth;

    if (numberOfSearchResultsInDocument !== null) {
      classSearchGoToButtonGroup = classNames(
        classSearchGoToButtonGroup,
        SearchInputDarkThemeUI.SearchGoToButtonGroupActive
      );
      if (!getIsInPlaceMode(stores)) {
        classSearchGoToButtonGroup = classNames(classSearchGoToButtonGroup, classSearchGoToButtonGroupActive);
      }
    }
    const searchInputClassNameCombined = classNames({
      [SearchInputUICss.SearchInputUI]: true,
      [searchInputClassName]: true
    });

    let searchIcon = null;
    const isOldViewerInPlaceMode = !this.props.useModernViewer && getIsInPlaceMode(stores);
    const isOldViewerInPlaceOrMobileMode = !this.props.useModernViewer && (getIsInPlaceMode(stores) || isMobile);

    if (!isOldViewerInPlaceOrMobileMode)
      searchIcon = (
        <div className={SearchInputUICss.SearchIconInputBox}>
          <SearchIcon />
        </div>
      );

    if (this.props.shouldUseMobileModernViewer) {
      searchIcon = (
        <div className={SearchInputUICss.SearchIconInputBoxMobile}>
          <SearchIconMobile />
        </div>
      );
    }

    const searchAddOnPanelCss = {
      position: "fixed",
      top: `${parseInt(searchInputStyle.marginTop) + 132}px`,
      right: availableMaxRight,
      marginRight,
      zIndex: 20099
    };

    let SearchTextInputStyle = {};
    if (isOldViewerInPlaceMode) {
      SearchTextInputStyle.padding = "0px";
      SearchTextInputStyle.minWidth = "70px";
    }
    return (
      <div>
        <div
          className={searchInputClassNameCombined}
          style={searchInputStyle}
          ref={ref => (this.rootElement = ref)}
          role="search"
        >
          <FormattedMessage id={this.props.useModernViewer ? "find.placeholder" : "find.placeholderOld"}>
            {placeholder => (
              <div className={searchWidgetClassName} style={searchWidgetStyleTheme}>
                {!isOldViewerInPlaceMode ? (
                  <Search
                    placeholder={placeholder}
                    quiet
                    icon={searchIcon}
                    value={this.state.searchText}
                    className={classSearchUI}
                    onChange={(queryString, event, triggerFrom) => {
                      this.setState({ searchText: queryString });
                      if (this.state.hideAISuggestionOnEnter) {
                        this.setState({ hideAISuggestionOnEnter: false });
                      }
                      this.props.onChange(queryString, event, triggerFrom);
                    }}
                    onSubmit={queryString => this.props.onSubmit(queryString)}
                    onFocus={this.searchFieldFocusHandler}
                    onBlur={this.searchFieldBlurHandler}
                    style={SearchTextInputStyle}
                    data-testid="search-input"
                  />
                ) : (
                  <Textfield
                    placeholder={placeholder}
                    quiet
                    defaultValue={this.props.defaultSearchQuery}
                    className={classSearchUI}
                    onChange={(queryString, event, triggerFrom) => this.props.onChange(queryString, event, triggerFrom)}
                    style={SearchTextInputStyle}
                    onFocus={this.searchFieldFocusHandler}
                    onBlur={this.searchFieldBlurHandler}
                    data-testid="search-input"
                  />
                )}
                {isOldViewerInPlaceMode ? numberOfSearchResultsInDocument : null}
                {this.props.totalSearchResultsCount ? (
                  <span aria-live="assertive" className={SearchInputUICss.hiddenSpan}>
                    {this.props.occurrencesString}
                  </span>
                ) : null}
              </div>
            )}
          </FormattedMessage>
          {isMobile && !this.props.showNumberOfSearchResults && !getIsInPlaceMode(stores) ? (
            <div className={SearchInputUICss.SearchGoToButtonGroup}>{numberOfSearchResultsInDocument}</div>
          ) : (
            <div className={classSearchGoToButtonGroup}>
              {!isOldViewerInPlaceMode ? numberOfSearchResultsInDocument : null}
              {this.props.useModernViewer ? (
                <ButtonWrapper
                  icon={this.props.previousIcon}
                  className={classPreviousIcon}
                  ariaLabel="arialabel.previous"
                  clickHandler={this.previousClicked}
                  shouldDisable={this.props.disablePreviousSearchButton}
                  tooltipString="tooltip.previous"
                  tooltipPlacement={TOOLTIP_BOTTOM_PLACEMENT}
                  tooltipDelay={TOOLTIP_DELAY}
                />
              ) : (
                <FormattedMessage id="tooltip.previous">
                  {previousTooltip => (
                    <div title={previousTooltip}>
                      <ButtonWrapper
                        icon={this.props.previousIcon}
                        className={classPreviousIcon}
                        ariaLabel="arialabel.previous"
                        clickHandler={this.previousClicked}
                        shouldDisable={this.props.disablePreviousSearchButton}
                      />
                    </div>
                  )}
                </FormattedMessage>
              )}

              {this.props.useModernViewer ? (
                <ButtonWrapper
                  icon={this.props.nextIcon}
                  ariaLabel="arialabel.next"
                  clickHandler={this.nextClicked}
                  className={classNextIcon}
                  shouldDisable={this.props.disableNextSearchButton}
                  tooltipString="tooltip.next"
                  tooltipPlacement={TOOLTIP_BOTTOM_PLACEMENT}
                  tooltipDelay={TOOLTIP_DELAY}
                />
              ) : (
                <FormattedMessage id="tooltip.next">
                  {nextTooltip => (
                    <div title={nextTooltip}>
                      <ButtonWrapper
                        icon={this.props.nextIcon}
                        ariaLabel="arialabel.next"
                        clickHandler={this.nextClicked}
                        className={classNextIcon}
                        shouldDisable={this.props.disableNextSearchButton}
                      />
                    </div>
                  )}
                </FormattedMessage>
              )}
            </div>
          )}
          {this.shouldShowTripleDot() && (
            <MenuTrigger
              direction="bottom"
              align="start"
              className={SearchInputUICss.menuTrigger}
              data-testid="triple-dot-menu-trigger"
              onOpenChange={isOpened => {
                this.setState({ isTripleDotClicked: isOpened });
              }}
            >
              <ActionButton
                isQuiet
                alignSelf="center"
                height="size-300"
                width="size-300"
                minWidth="size-300"
                data-testid="triple-dot-action-button"
                onPress={() => {
                  trackSearchTripleDotMenuClicked();
                }}
              >
                <Icon>
                  <More />
                </Icon>
              </ActionButton>
              <Menu
                onAction={key => {
                  const menuItem = this.state.addOnMenuItems.find(item => item.key === key);
                  if (menuItem && menuItem.action) menuItem.action(this.rootElement);
                  setTimeout(() => {
                    this.setState({ addOnMenuItems: searchAddOnPanel.getTripleDotMenuItems() });
                  }, SEARCH_MENU_ITEMS_UPDATE_TIMEOUT);
                }}
                data-testid="triple-dot-menu"
              >
                {this.state.addOnMenuItems.map(menuItem => (
                  <Item key={menuItem.key} textValue={menuItem.name}>
                    {menuItem.icon ? (
                      <Icon>
                        <CustomIcon url={menuItem.icon} />
                      </Icon>
                    ) : null}
                    <Text> {menuItem.name}</Text>
                  </Item>
                ))}
              </Menu>
            </MenuTrigger>
          )}
          <CancelSearchButton
            useModernViewer={this.props.useModernViewer}
            useMobileModernViewer={this.props.shouldUseMobileModernViewer}
            appContext={this.props.appContext}
            color={this.props.options.color}
            onClick={() => this.closeClickHandler()}
            ariaLabel="find.close"
            tooltipString={this.props.useModernViewer ? "find.close" : "find.closeOld"}
            tooltipPlacement={TOOLTIP_BOTTOM_PLACEMENT}
            tooltipDelay={TOOLTIP_DELAY}
          />
        </div>

        {this.shouldShowSearchAddOnPanel() ? (
          <div style={searchAddOnPanelCss}>
            <this.state.addOnPanelComponent />
          </div>
        ) : null}
      </div>
    );
  }
}

SearchInputUI.defaultProps = {
  searchInputClassName: "",
  searchWidgetClassName: "",
  searchInputStyle: {},
  searchWidgetStyle: {}
};

export { SearchInputUI };
